/**
 * BPMN 2.0 XML Exporter
 *
 * Converts Creately process diagram nodes/edges into BPMN 2.0 XML
 * compatible with IBM BlueWorks Live, Camunda Modeler, and bpmn.io.
 */

import type { Node, Edge } from '../../../core/models/scene';
import { getLaneLabelForNode } from '../services/laneContainment';

// ─── XML helpers ────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function attr(name: string, value: string | number | boolean | undefined): string {
  if (value === undefined) return '';
  return ` ${name}="${esc(String(value))}"`;
}

function indent(depth: number): string {
  return '    '.repeat(depth);
}

// ─── Node type resolution ───────────────────────────────────────

interface BpmnElement {
  tag: string;
  eventDefinition?: string;
  isMarkerVisible?: boolean;
}

function getAttrs(node: Node): Record<string, unknown> {
  const a = (node as any).attrs ?? (node as any).props?.attrs ?? {};
  return a;
}

function getNodeLabel(node: Node): string {
  return (node as any).text ?? (node as any).name ?? (node as any).attrs?.label ?? node.id;
}

type ExportEdge = Edge & {
  attrs?: Record<string, unknown>;
  label?: string;
  text?: string;
};

function getEdgeLabel(edge: Edge): string | undefined {
  const candidate = edge as ExportEdge;
  const label = candidate.attrs?.label ?? candidate.label ?? candidate.text;
  return typeof label === 'string' ? label : undefined;
}

function resolveFlowchartShape(node: Node, edges: Edge[]): BpmnElement {
  const attrs = getAttrs(node);
  const variant = attrs.variant as string | undefined;

  switch (variant) {
    case 'decision':
      return { tag: 'exclusiveGateway', isMarkerVisible: true };
    case 'document':
    case 'multi-doc':
      return { tag: 'dataObjectReference' };
    case 'database':
    case 'storage':
      return { tag: 'dataStoreReference' };
    case 'start-end':
    case 'terminator': {
      const hasIncoming = edges.some(e => e.to === node.id);
      const hasOutgoing = edges.some(e => e.from === node.id);
      if (!hasIncoming && hasOutgoing) return { tag: 'startEvent' };
      if (hasIncoming && !hasOutgoing) return { tag: 'endEvent' };
      // Ambiguous — default to event based on label
      const label = getNodeLabel(node).toLowerCase();
      if (label.includes('start') || label.includes('begin')) return { tag: 'startEvent' };
      return { tag: 'endEvent' };
    }
    default:
      return { tag: 'task' };
  }
}

function resolveBpmnShape(node: Node): BpmnElement {
  const attrs = getAttrs(node);
  const variant = attrs.variant as string;
  const trigger = attrs.trigger as string | undefined;

  const triggerToDefinition: Record<string, string> = {
    timer: 'timerEventDefinition',
    message: 'messageEventDefinition',
    error: 'errorEventDefinition',
    signal: 'signalEventDefinition',
    conditional: 'conditionalEventDefinition',
    terminate: 'terminateEventDefinition',
    escalation: 'escalationEventDefinition',
    cancel: 'cancelEventDefinition',
    compensation: 'compensateEventDefinition',
    link: 'linkEventDefinition',
  };

  switch (variant) {
    case 'bpmn-event': {
      const eventType = attrs.eventType as string | undefined;
      const eventMode = attrs.eventMode as string | undefined;
      const eventDef = trigger ? triggerToDefinition[trigger] : undefined;

      if (eventType === 'start') return { tag: 'startEvent', eventDefinition: eventDef };
      if (eventType === 'end') return { tag: 'endEvent', eventDefinition: eventDef };
      if (eventMode === 'throwing') return { tag: 'intermediateThrowEvent', eventDefinition: eventDef };
      return { tag: 'intermediateCatchEvent', eventDefinition: eventDef };
    }
    case 'bpmn-gateway': {
      const gwType = attrs.gatewayType as string | undefined;
      if (gwType === 'parallel') return { tag: 'parallelGateway' };
      if (gwType === 'inclusive') return { tag: 'inclusiveGateway' };
      if (gwType === 'event-based') return { tag: 'eventBasedGateway' };
      return { tag: 'exclusiveGateway', isMarkerVisible: true };
    }
    case 'bpmn-activity': {
      const taskType = attrs.taskType as string | undefined;
      const taskMap: Record<string, string> = {
        user: 'userTask',
        service: 'serviceTask',
        manual: 'manualTask',
        send: 'sendTask',
        receive: 'receiveTask',
        script: 'scriptTask',
        'business-rule': 'businessRuleTask',
      };
      const activityType = attrs.activityType as string | undefined;
      if (activityType === 'sub-process') return { tag: 'subProcess' };
      if (activityType === 'call-activity') return { tag: 'callActivity' };
      return { tag: taskType ? (taskMap[taskType] ?? 'task') : 'task' };
    }
    case 'bpmn-data':
      return { tag: 'dataObjectReference' };
    case 'bpmn-data-store':
      return { tag: 'dataStoreReference' };
    case 'bpmn-annotation':
      return { tag: 'textAnnotation' };
    default:
      return { tag: 'task' };
  }
}

function resolveBpmnElement(node: Node, edges: Edge[]): BpmnElement {
  const type = (node as any).type as string;
  if (type === 'bpmn-shape') return resolveBpmnShape(node);
  if (type === 'flowchart-shape') return resolveFlowchartShape(node, edges);
  // Legacy direct types
  if (type === 'process') return { tag: 'task' };
  if (type === 'decision') return { tag: 'exclusiveGateway', isMarkerVisible: true };
  if (type === 'terminator') return resolveFlowchartShape(node, edges);
  if (type === 'document' || type === 'data') return { tag: 'dataObjectReference' };
  return { tag: 'task' };
}

// ─── Flow element tags that are actual BPMN flow nodes ──────────

const FLOW_NODE_TAGS = new Set([
  'task', 'userTask', 'serviceTask', 'manualTask', 'sendTask', 'receiveTask',
  'scriptTask', 'businessRuleTask', 'subProcess', 'callActivity',
  'startEvent', 'endEvent', 'intermediateCatchEvent', 'intermediateThrowEvent',
  'exclusiveGateway', 'parallelGateway', 'inclusiveGateway', 'eventBasedGateway',
]);

// ─── Main export function ───────────────────────────────────────

export interface BpmnExportOptions {
  processName?: string;
  includeLayout?: boolean;
}

export function exportToBpmnXml(
  nodes: Node[],
  edges: Edge[],
  options: BpmnExportOptions = {},
): string {
  const { processName = 'Process', includeLayout = true } = options;

  // Filter out pool table, keep only process/BPMN nodes
  const processNodes = nodes.filter((n: any) => {
    const type = n.type as string;
    const attrs = getAttrs(n);
    if (attrs.__isPoolTable) return false;
    return type === 'flowchart-shape' || type === 'bpmn-shape' ||
           type === 'process' || type === 'decision' || type === 'terminator' ||
           type === 'document' || type === 'data';
  });

  const processEdges = edges.filter((e: any) => {
    const type = e.type as string;
    return type === 'sequence_flow' || type === 'message_flow' ||
           type === 'exception_flow' || type === 'data_association';
  });

  // Resolve each node to a BPMN element
  const resolved = new Map<string, { node: Node; element: BpmnElement; label: string }>();
  for (const node of processNodes) {
    resolved.set(node.id, {
      node,
      element: resolveBpmnElement(node, processEdges),
      label: getNodeLabel(node),
    });
  }
  const validProcessEdges = processEdges.filter(
    (edge) => resolved.has(edge.from) && resolved.has(edge.to),
  );
  const messageEdges = validProcessEdges.filter((edge) => (edge.type as string) === 'message_flow');
  const sequenceEdges = validProcessEdges.filter((edge) => (edge.type as string) !== 'message_flow');

  // Swimlane detection
  const poolTable = nodes.find((n: any) => getAttrs(n).__isPoolTable);
  const hasLanes = !!poolTable;
  const laneGroups = new Map<string, string[]>(); // label → node IDs

  if (hasLanes) {
    for (const [id, info] of resolved) {
      const laneLabel = getLaneLabelForNode(info.node, nodes);
      if (!laneLabel) continue;
      if (!laneGroups.has(laneLabel)) laneGroups.set(laneLabel, []);
      laneGroups.get(laneLabel)!.push(id);
    }
  }

  // Compute incoming/outgoing for each node (for DI edge waypoints)
  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  for (const edge of sequenceEdges) {
    if (!outgoing.has(edge.from)) outgoing.set(edge.from, []);
    outgoing.get(edge.from)!.push(edge.id);
    if (!incoming.has(edge.to)) incoming.set(edge.to, []);
    incoming.get(edge.to)!.push(edge.id);
  }

  // ─── Build XML ─────────────────────────────────────────────

  const lines: string[] = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push('<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL"');
  lines.push('             xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"');
  lines.push('             xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"');
  lines.push('             xmlns:di="http://www.omg.org/spec/DD/20100524/DI"');
  lines.push('             xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"');
  lines.push(`             id="definitions_1" targetNamespace="http://creately.com/bpmn">`);

  // Collaboration (for pools/lanes)
  if (hasLanes || messageEdges.length > 0) {
    lines.push(`  <collaboration id="collaboration_1">`);
    if (hasLanes) {
      lines.push(`    <participant id="participant_1"${attr('name', processName)} processRef="process_1"/>`);
    }
    for (const edge of messageEdges) {
      const label = getEdgeLabel(edge);
      lines.push(`    <messageFlow id="${esc(edge.id)}"${attr('name', label)} sourceRef="${esc(edge.from)}" targetRef="${esc(edge.to)}"/>`);
    }
    lines.push(`  </collaboration>`);
  }

  // Process
  lines.push(`  <process id="process_1"${attr('name', processName)} isExecutable="false">`);

  // Lane set
  if (hasLanes) {
    lines.push(`    <laneSet id="laneSet_1">`);
    let laneIdx = 0;
    for (const [label, nodeIds] of laneGroups) {
      const laneId = `lane_${laneIdx++}`;
      lines.push(`      <lane id="${laneId}"${attr('name', label)}>`);
      for (const nid of nodeIds) {
        const info = resolved.get(nid);
        if (info && FLOW_NODE_TAGS.has(info.element.tag)) {
          lines.push(`        <flowNodeRef>${esc(nid)}</flowNodeRef>`);
        }
      }
      lines.push(`      </lane>`);
    }
    lines.push(`    </laneSet>`);
  }

  // Flow nodes
  for (const [id, info] of resolved) {
    const { element, label } = info;
    const nodeIncoming = incoming.get(id) ?? [];
    const nodeOutgoing = outgoing.get(id) ?? [];

    if (element.tag === 'textAnnotation') {
      lines.push(`    <textAnnotation id="${esc(id)}">`);
      lines.push(`      <text>${esc(label)}</text>`);
      lines.push(`    </textAnnotation>`);
      continue;
    }

    if (!FLOW_NODE_TAGS.has(element.tag) && element.tag !== 'dataObjectReference' && element.tag !== 'dataStoreReference') {
      continue;
    }

    const incomingAttr = nodeIncoming.length > 0
      ? nodeIncoming.map(eid => `\n${indent(2)}<incoming>${esc(eid)}</incoming>`).join('')
      : '';
    const outgoingAttr = nodeOutgoing.length > 0
      ? nodeOutgoing.map(eid => `\n${indent(2)}<outgoing>${esc(eid)}</outgoing>`).join('')
      : '';

    const hasChildren = !!element.eventDefinition || incomingAttr || outgoingAttr;

    if (hasChildren) {
      lines.push(`    <${element.tag} id="${esc(id)}"${attr('name', label)}>${incomingAttr}${outgoingAttr}`);
      if (element.eventDefinition) {
        lines.push(`      <${element.eventDefinition}/>`);
      }
      lines.push(`    </${element.tag}>`);
    } else {
      lines.push(`    <${element.tag} id="${esc(id)}"${attr('name', label)}/>`);
    }
  }

  // Sequence flows
  for (const edge of sequenceEdges) {
    const edgeAny = edge as any;
    const label = edgeAny.attrs?.label ?? edgeAny.label ?? edgeAny.text;
    const condExpr = edgeAny.attrs?.condition ?? edgeAny.attrs?.conditionExpression;

    if (condExpr) {
      lines.push(`    <sequenceFlow id="${esc(edge.id)}"${attr('name', label)} sourceRef="${esc(edge.from)}" targetRef="${esc(edge.to)}">`);
      lines.push(`      <conditionExpression xsi:type="tFormalExpression">${esc(String(condExpr))}</conditionExpression>`);
      lines.push(`    </sequenceFlow>`);
    } else {
      lines.push(`    <sequenceFlow id="${esc(edge.id)}"${attr('name', label)} sourceRef="${esc(edge.from)}" targetRef="${esc(edge.to)}"/>`);
    }
  }

  lines.push(`  </process>`);

  // BPMNDiagram (layout coordinates)
  if (includeLayout) {
    const bpmnElement = hasLanes || messageEdges.length > 0 ? 'collaboration_1' : 'process_1';
    lines.push(`  <bpmndi:BPMNDiagram id="diagram_1">`);
    lines.push(`    <bpmndi:BPMNPlane id="plane_1" bpmnElement="${bpmnElement}">`);

    // Pool shape
    if (hasLanes && poolTable) {
      const pt = poolTable as any;
      const px = pt.x ?? 0;
      const py = pt.y ?? 0;
      const pw = pt.w ?? pt.width ?? 800;
      const ph = pt.h ?? pt.height ?? 400;
      lines.push(`      <bpmndi:BPMNShape id="participant_1_di" bpmnElement="participant_1" isHorizontal="true">`);
      lines.push(`        <dc:Bounds x="${px}" y="${py}" width="${pw}" height="${ph}"/>`);
      lines.push(`      </bpmndi:BPMNShape>`);

      // Lane shapes
      let laneIdx = 0;
      const ptAttrs = getAttrs(poolTable);
      const rowHeights = (ptAttrs.rowHeights as number[]) ?? [];
      const laneDirection = (ptAttrs.laneDirection as string) ?? 'horizontal';
      let laneY = py;

      for (const [, ] of laneGroups) {
        const laneId = `lane_${laneIdx}`;
        const laneH = rowHeights[laneIdx] ?? (ph / laneGroups.size);
        if (laneDirection === 'horizontal') {
          lines.push(`      <bpmndi:BPMNShape id="${laneId}_di" bpmnElement="${laneId}" isHorizontal="true">`);
          lines.push(`        <dc:Bounds x="${px}" y="${laneY}" width="${pw}" height="${laneH}"/>`);
          lines.push(`      </bpmndi:BPMNShape>`);
          laneY += laneH;
        }
        laneIdx++;
      }
    }

    // Node shapes
    for (const [id, info] of resolved) {
      const n = info.node as any;
      const x = n.x ?? 0;
      const y = n.y ?? 0;
      const w = n.w ?? n.width ?? 120;
      const h = n.h ?? n.height ?? 80;
      const extraAttrs = info.element.isMarkerVisible ? ' isMarkerVisible="true"' : '';
      lines.push(`      <bpmndi:BPMNShape id="${esc(id)}_di" bpmnElement="${esc(id)}"${extraAttrs}>`);
      lines.push(`        <dc:Bounds x="${x}" y="${y}" width="${w}" height="${h}"/>`);
      lines.push(`      </bpmndi:BPMNShape>`);
    }

    // Edge shapes
    for (const edge of validProcessEdges) {
      const fromInfo = resolved.get(edge.from);
      const toInfo = resolved.get(edge.to);
      if (!fromInfo || !toInfo) continue;

      const fn = fromInfo.node as any;
      const tn = toInfo.node as any;

      // Use anchor info if available for better waypoints
      const eAny = edge as any;
      const fromAnchor = eAny.fromAnchor as string | undefined;
      const toAnchor = eAny.toAnchor as string | undefined;
      const { x: sx, y: sy } = anchorPoint(fn, fromAnchor);
      const { x: ex, y: ey } = anchorPoint(tn, toAnchor);

      lines.push(`      <bpmndi:BPMNEdge id="${esc(edge.id)}_di" bpmnElement="${esc(edge.id)}">`);
      lines.push(`        <di:waypoint x="${sx}" y="${sy}"/>`);
      // Add midpoint for orthogonal routing when anchors are on different axes
      if ((fromAnchor === 'right' || fromAnchor === 'left') && (toAnchor === 'top' || toAnchor === 'bottom')) {
        lines.push(`        <di:waypoint x="${ex}" y="${sy}"/>`);
      } else if ((fromAnchor === 'top' || fromAnchor === 'bottom') && (toAnchor === 'right' || toAnchor === 'left')) {
        lines.push(`        <di:waypoint x="${sx}" y="${ey}"/>`);
      }
      lines.push(`        <di:waypoint x="${ex}" y="${ey}"/>`);
      lines.push(`      </bpmndi:BPMNEdge>`);
    }

    lines.push(`    </bpmndi:BPMNPlane>`);
    lines.push(`  </bpmndi:BPMNDiagram>`);
  }

  lines.push('</definitions>');
  return lines.join('\n');
}

// ─── Anchor point calculation ───────────────────────────────────

function anchorPoint(
  node: any,
  anchor?: string,
): { x: number; y: number } {
  const x = (node.x ?? 0) as number;
  const y = (node.y ?? 0) as number;
  const w = (node.w ?? node.width ?? 120) as number;
  const h = (node.h ?? node.height ?? 80) as number;

  switch (anchor) {
    case 'right': return { x: x + w, y: y + h / 2 };
    case 'left': return { x, y: y + h / 2 };
    case 'top': return { x: x + w / 2, y };
    case 'bottom': return { x: x + w / 2, y: y + h };
    default: return { x: x + w / 2, y: y + h / 2 };
  }
}
